import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { getCommentsByTicket, createComment, updateComment, deleteComment } from '../../../api/ticketComments';
import { getAuditLogs } from '../../../api/auditLog';
import { canEditComment, canDeleteComment } from '../../../middleware/betaPermissions';
import { useConfirm } from '../../common/ConfirmDialog';
import MentionInput from './MentionInput';
import MarkdownViewer from '../../common/MarkdownViewer';
import Avatar from '../../common/Avatar';
import { formatDistanceToNow } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import './TicketComments.scss';

const formatFullTime = (dateStr) => {
  return new Date(dateStr).toLocaleString('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
};

// 狀態值 → 顯示標籤的對照（fallback，優先用 statusOptions）
const STATUS_LABEL_FALLBACK = {
  open: '開放',
  in_progress: '進行中',
  completed: '已完成',
  closed: '已關閉',
};

const TicketComments = forwardRef(({ ticketId, refreshKey = 0, onTicketClick, companyId, onCommentsChange }, ref) => {
  const { profile } = useAuth();
  const { confirm } = useConfirm();
  const { usersMapByEmail, statusOptions } = useBetaData();
  const [timelineItems, setTimelineItems] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  const loadTimeline = useCallback(async () => {
    setLoading(true);

    const [commentsRes, activitiesRes] = await Promise.all([
      getCommentsByTicket(ticketId),
      getAuditLogs({
        resourceType: 'ticket',
        resourceId: String(ticketId),
        limit: 100,
      }),
    ]);

    const comments = (commentsRes.data || []).map((c) => ({ ...c, _type: 'comment' }));
    const activityActions = new Set(['assign', 'unassign', 'assignment_change', 'status_change']);
    const activities = (activitiesRes.data || [])
      .filter((log) => activityActions.has(log.action))
      .map((a) => ({ ...a, _type: 'activity' }));

    const merged = [...comments, ...activities].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    setTimelineItems(merged);
    setCommentCount(comments.length);
    setLoading(false);
  }, [ticketId]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline, refreshKey]);

  // 讓外部透過 ref 直接 push activity（不 re-fetch）
  useImperativeHandle(ref, () => ({
    appendActivity: (activity) => {
      setTimelineItems((prev) => [...prev, { ...activity, _type: 'activity' }]);
    },
  }), []);

  const handleSubmit = async () => {
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    const { data, error } = await createComment({
      ticket_id: ticketId,
      author_id: profile.id,
      content: newComment.trim(),
    });

    if (!error && data) {
      setTimelineItems((prev) => [...prev, { ...data, _type: 'comment' }]);
      setCommentCount((c) => c + 1);
      setNewComment('');
      onCommentsChange?.();
    }
    setSubmitting(false);
  };

  const handleEdit = (comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const handleEditSave = async (commentId) => {
    if (!editContent.trim()) return;
    const { data } = await updateComment(commentId, editContent.trim());
    if (data) {
      setTimelineItems((prev) => prev.map((item) => (item._type === 'comment' && item.id === commentId ? { ...data, _type: 'comment' } : item)));
      onCommentsChange?.();
    }
    setEditingId(null);
    setEditContent('');
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleDelete = async (commentId) => {
    const confirmed = await confirm({
      title: '刪除留言',
      message: '確定要刪除此留言嗎？此操作無法復原。',
      confirmText: '刪除',
      type: 'danger',
    });
    if (!confirmed) return;
    const { error } = await deleteComment(commentId);
    if (!error) {
      setTimelineItems((prev) => prev.filter((item) => !(item._type === 'comment' && item.id === commentId)));
      setCommentCount((c) => c - 1);
      onCommentsChange?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  return (
    <div className="ticket-comments">
      <h3 className="comments-title">
        留言與動態
        <span className="comments-count">{commentCount}</span>
      </h3>

      {/* Timeline list */}
      <div className="comments-list">
        {loading ? (
          <div className="comments-skeleton">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-comment">
                <div className="skeleton-avatar" />
                <div className="skeleton-content">
                  <div className="skeleton-line w30 h12" />
                  <div className="skeleton-line w80 h14" />
                </div>
              </div>
            ))}
          </div>
        ) : timelineItems.length === 0 ? (
          <div className="comments-empty">尚無留言</div>
        ) : (
          timelineItems.map((item) => {
            if (item._type === 'activity') {
              const actorName = item.user_name || '未知';
              let activityContent = null;
              let iconType = 'user'; // 'user' | 'status'

              if (item.action === 'assignment_change') {
                // 新格式：合併 added / removed
                const added = item.metadata?.added || [];
                const removed = item.metadata?.removed || [];
                const addedNames = added.map((u) => u.user_name).filter(Boolean);
                const removedNames = removed.map((u) => u.user_name).filter(Boolean);

                if (item.metadata?.type === 'owner_swap' && addedNames.length > 0) {
                  // Owner 交換
                  activityContent = (
                    <>
                      {' 將 Owner 改為 '}
                      <span className="target-name">@{addedNames[0]}</span>
                    </>
                  );
                } else if (removedNames.length > 0 && addedNames.length > 0) {
                  activityContent = (
                    <>
                      {' 取消指派 '}
                      {removedNames.map((n, i) => (
                        <span key={`r-${i}`}>{i > 0 && '、'}<span className="target-name">@{n}</span></span>
                      ))}
                      {' 並新增指派給 '}
                      {addedNames.map((n, i) => (
                        <span key={`a-${i}`}>{i > 0 && '、'}<span className="target-name">@{n}</span></span>
                      ))}
                    </>
                  );
                } else if (addedNames.length > 0) {
                  activityContent = (
                    <>
                      {' 新增指派給 '}
                      {addedNames.map((n, i) => (
                        <span key={`a-${i}`}>{i > 0 && '、'}<span className="target-name">@{n}</span></span>
                      ))}
                    </>
                  );
                } else if (removedNames.length > 0) {
                  activityContent = (
                    <>
                      {' 取消指派 '}
                      {removedNames.map((n, i) => (
                        <span key={`r-${i}`}>{i > 0 && '、'}<span className="target-name">@{n}</span></span>
                      ))}
                    </>
                  );
                }
              } else if (item.action === 'assign') {
                // 舊格式相容
                activityContent = (
                  <>
                    {' 新增指派給 '}
                    <span className="target-name">@{item.metadata?.target_user_name || '未知'}</span>
                  </>
                );
              } else if (item.action === 'unassign') {
                // 舊格式相容
                activityContent = (
                  <>
                    {' 取消指派 '}
                    <span className="target-name">@{item.metadata?.target_user_name || '未知'}</span>
                  </>
                );
              } else if (item.action === 'status_change') {
                iconType = 'status';
                const oldOption = statusOptions?.find((s) => s.value === item.metadata?.old_status);
                const newOption = statusOptions?.find((s) => s.value === item.metadata?.new_status);
                const oldLabel = oldOption?.label || STATUS_LABEL_FALLBACK[item.metadata?.old_status] || item.metadata?.old_status;
                const newLabel = newOption?.label || STATUS_LABEL_FALLBACK[item.metadata?.new_status] || item.metadata?.new_status;
                const oldColor = oldOption?.color || '#94a3b8';
                const newColor = newOption?.color || '#94a3b8';
                activityContent = (
                  <>
                    {' 將狀態從 '}
                    <span className="status-label" style={{ backgroundColor: oldColor + '1a', color: oldColor }}>{oldLabel}</span>
                    {' 改為 '}
                    <span className="status-label" style={{ backgroundColor: newColor + '1a', color: newColor }}>{newLabel}</span>
                  </>
                );
              }

              if (!activityContent) return null;

              return (
                <div key={`activity-${item.id}`} className={`activity-row ${iconType === 'status' ? 'status-change' : ''}`}>
                  <div className="activity-icon">
                    {iconType === 'status' ? (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm3.7 5.7l-4 4a1 1 0 01-1.4 0l-2-2a1 1 0 111.4-1.4L7 8.58l3.3-3.3a1 1 0 011.4 1.42z" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm5 6a5 5 0 00-10 0h10z" />
                      </svg>
                    )}
                  </div>
                  <span className="activity-text">
                    <span className="actor-name">{actorName}</span>
                    {activityContent}
                  </span>
                  <span className="activity-time" title={formatFullTime(item.created_at)}>
                    {formatDistanceToNow(new Date(item.created_at), {
                      addSuffix: true,
                      locale: zhTW,
                    })}
                  </span>
                </div>
              );
            }

            const comment = item;
            const authorEmail = comment.author?.email?.toLowerCase();
            const resolvedAuthor = authorEmail ? usersMapByEmail[authorEmail] : null;
            const authorName = resolvedAuthor?.name || comment.author?.email || '未知';
            return (
            <div key={comment.id} className="comment-item">
              <Avatar
                avatarUrl={resolvedAuthor?.avatar_url}
                name={authorName}
                size="small"
              />
              <div className="comment-body">
                <div className="comment-header">
                  <span className="comment-author">{authorName}</span>
                  <span className="comment-time" title={formatFullTime(comment.created_at)}>
                    {formatDistanceToNow(new Date(comment.created_at), {
                      addSuffix: true,
                      locale: zhTW,
                    })}
                    {comment.updated_at && comment.updated_at !== comment.created_at && ' (已編輯)'}
                  </span>
                  {canEditComment(profile, comment) && editingId !== comment.id && (
                    <button
                      className="comment-edit"
                      onClick={() => handleEdit(comment)}
                      title="編輯"
                    >
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M10 1.5l2.5 2.5M1 13l.8-3.2L10.3 1.3a1 1 0 011.4 0l1 1a1 1 0 010 1.4L4.2 12.2 1 13z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                  {canDeleteComment(profile, comment) && (
                    <button
                      className="comment-delete"
                      onClick={() => handleDelete(comment.id)}
                      title="刪除"
                    >
                      &times;
                    </button>
                  )}
                </div>
                {editingId === comment.id ? (
                  <div className="comment-edit-area">
                    <MentionInput
                      value={editContent}
                      onChange={setEditContent}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleEditSave(comment.id);
                        if (e.key === 'Escape') handleEditCancel();
                      }}
                      placeholder="編輯留言..."
                      departmentId={profile?.department_id}
                      companyId={companyId}
                    />
                    <div className="comment-edit-actions">
                      <button className="btn-save" onClick={() => handleEditSave(comment.id)}>儲存</button>
                      <button className="btn-cancel" onClick={handleEditCancel}>取消</button>
                    </div>
                  </div>
                ) : (
                  <div className="comment-content">
                    <MarkdownViewer content={comment.content} onTicketClick={onTicketClick} />
                  </div>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* New comment input */}
      <div className="comment-input-area">
        <Avatar
          avatarUrl={profile?.avatar_url}
          name={profile?.name}
          size="small"
        />
        <div className="input-wrapper">
          <MentionInput
            value={newComment}
            onChange={setNewComment}
            onKeyDown={handleKeyDown}
            placeholder="留言（Ctrl+Enter 送出，@提及，#引用 Ticket）..."
            departmentId={profile?.department_id}
            companyId={companyId}
          />
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
          >
            {submitting ? '送出中...' : '留言'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default TicketComments;
